import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from '@/components/ui/sonner';
import WizardProgressIndicator from './WizardProgressIndicator';
import CoursePreSelectionStep from './CoursePreSelectionStep';
import StudentDetailsStep, { StudentFormData } from './StudentDetailsStep';
import AccountSummaryStep from './AccountSummaryStep';
import { supabase } from '@/integrations/supabase/client';

interface StudentCreationWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

const WIZARD_STEPS = [
  {
    id: 'courses',
    title: 'Select Courses',
    description: 'Choose courses to assign'
  },
  {
    id: 'details',
    title: 'Student Details',
    description: 'Enter student information'
  },
  {
    id: 'summary',
    title: 'Review & Create',
    description: 'Confirm and create account'
  }
];

const StudentCreationWizard: React.FC<StudentCreationWizardProps> = ({
  onClose,
  onSuccess
}) => {
  const { session } = useSupabaseAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [studentData, setStudentData] = useState<StudentFormData>({
    name: '',
    email: '',
    department: '',
    notes: '',
  });
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationResult, setCreationResult] = useState<{
    success: boolean;
    temporaryPassword?: string;
    message?: string;
  } | null>(null);

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCompletedSteps(prev => [...prev, currentStep]);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleStepEdit = (step: 'courses' | 'details') => {
    const stepIndex = WIZARD_STEPS.findIndex(s => s.id === step);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: // Courses step
        return true; // Allow proceeding without courses
      case 1: // Details step
        return studentData.name.trim() !== '' && 
               studentData.email.trim() !== '' && 
               /\S+@\S+\.\S+/.test(studentData.email);
      case 2: // Summary step
        return true;
      default:
        return false;
    }
  };

  const handleCreateAccount = async () => {
    if (!session?.user?.id) {
      toast.error('Authentication required');
      return;
    }

    setIsCreating(true);
    
    try {
      console.log('🧙 Wizard submission started:', {
        email: studentData.email,
        name: studentData.name,
        coursesSelected: selectedCourses.length,
        courseIds: selectedCourses,
        department: studentData.department,
        sendWelcomeEmail: sendWelcomeEmail,
        timestamp: new Date().toISOString()
      });

      // Call the invite-user edge function with courses
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: studentData.email,
          name: studentData.name,
          role: 'user',
          courseIds: selectedCourses,
          department: studentData.department,
          notes: studentData.notes,
          requirePasswordChange: true,
          createdViaWizard: true,
          sendWelcomeEmail: sendWelcomeEmail
        }
      });

      console.log('🧙 Edge function response:', {
        hasData: !!data,
        hasError: !!error,
        data: data,
        error: error,
        timestamp: new Date().toISOString()
      });

      if (error) {
        console.error('Edge function error details:', {
          error,
          data,
          type: typeof error,
          message: error?.message,
          timestamp: new Date().toISOString()
        });
        
        let errorMessage = 'Failed to create student account';
        
        // When edge function returns non-2xx, the response body is in 'data'
        // Check data first for the actual error message from the edge function
        if (data && typeof data === 'object') {
          if ('error' in data && typeof data.error === 'string') {
            errorMessage = data.error;
          } else if ('message' in data && typeof data.message === 'string') {
            errorMessage = data.message;
          }
        }
        
        // Fallback to error object if data didn't contain the message
        if (errorMessage === 'Failed to create student account' && error.message) {
          errorMessage = error.message;
        }
        
        // Add user-friendly context for duplicate users
        if (errorMessage.toLowerCase().includes('already exists')) {
          errorMessage = `${errorMessage}. Please check if this student was previously created.`;
        }
        
        console.error('Parsed error message:', errorMessage);
        throw new Error(errorMessage);
      }

      if (data?.success) {
        setCreationResult({
          success: true,
          temporaryPassword: data.temporaryPassword,
          message: data.message
        });
        toast.success('Student account created successfully!');
        setTimeout(() => {
          onSuccess();
        }, 5000); // Auto-close after 5 seconds to allow viewing credentials
      } else {
        const errorMsg = data?.error || data?.message || 'Failed to create student account';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Catch block - Error creating student account:', {
        error,
        type: typeof error,
        isError: error instanceof Error,
        message: error instanceof Error ? error.message : 'Unknown',
        stack: error instanceof Error ? error.stack : undefined,
        fullError: JSON.stringify(error, null, 2)
      });
      
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      setCreationResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <CoursePreSelectionStep
            onNext={() => setCurrentStep(1)}
            onBack={() => setCurrentStep(-1)}
            selectedCourses={selectedCourses}
            onCourseToggle={(courseId: string) => {
              setSelectedCourses(prev => 
                prev.includes(courseId) 
                  ? prev.filter(id => id !== courseId)
                  : [...prev, courseId]
              );
            }}
          />
        );
      case 1:
        return (
          <StudentDetailsStep
            selectedCourses={selectedCourses}
            onCoursesEdit={() => handleStepEdit('courses')}
            onFormChange={setStudentData}
            initialData={studentData}
          />
        );
      case 2:
        return (
          <AccountSummaryStep
            studentData={studentData}
            selectedCourses={selectedCourses}
            onEdit={handleStepEdit}
            isCreating={isCreating}
            sendWelcomeEmail={sendWelcomeEmail}
            onSendWelcomeEmailChange={setSendWelcomeEmail}
            creationResult={creationResult}
          />
        );
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    if (creationResult) {
      return (
        <div className="flex justify-between">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => {
            // Reset wizard for creating another student
            setCurrentStep(0);
            setCompletedSteps([]);
            setSelectedCourses([]);
            setStudentData({ name: '', email: '', department: '', notes: '' });
            setCreationResult(null);
          }}>
            Create Another Student
          </Button>
        </div>
      );
    }

    return (
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 0 ? onClose : handlePrevious}
          disabled={isCreating}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {currentStep === 0 ? 'Cancel' : 'Previous'}
        </Button>

        {currentStep === WIZARD_STEPS.length - 1 ? (
          <Button
            onClick={handleCreateAccount}
            disabled={!isCurrentStepValid() || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Student Account'
            )}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!isCurrentStepValid()}
          >
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold">Create New Student</h1>
        <p className="text-muted-foreground mt-2">
          Follow the steps below to create a new student account with course assignments.
        </p>
      </div>

      {/* Progress Indicator */}
      {!creationResult && (
        <WizardProgressIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      )}

      {/* Step Content */}
      <Card>
        <CardContent className="p-8">
          {renderCurrentStep()}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-center">
        <div className="w-full max-w-md">
          {getActionButtons()}
        </div>
      </div>
    </div>
  );
};

export default StudentCreationWizard;